/**
 * Miroir frontend du gate backend (App\Enum\AccessTier + App\Service\EntitlementService).
 * Fonctions pures, sans dépendance React, testables isolément.
 *
 * Les limites ne sont jamais écrites ici : elles viennent de quotas.generated.json,
 * écrit par `php bin/console app:entitlements:export` depuis kenometric.quotas
 * (config/services.yaml). Un test PHP (ExportEntitlementsCommandTest) casse si le
 * fichier diverge du service. Pour le membre connecté, useEntitlements() préfère le
 * bloc `entitlements` du statut de licence, calculé par le serveur.
 */
import quotas from "./quotas.generated.json";

export type AccessTier = "anonymous" | "free" | "premium";

/** Limites d'un niveau, mêmes clés que EntitlementService::limitsFor(). */
export interface AccessLimits {
  /**
   * Fenêtre d'une famille fermée de la frontière (comportement d'avant la frontière) :
   * tirages les plus récents servis ; null = tout l'historique.
   */
  tirageDepth: number | null;
  /**
   * Fenêtre de calcul d'une famille ouverte : nombre de tirages, « all » (tout l'historique)
   * ou « none » (aucun calcul, l'anonyme). Lire par fenetreCalcul() de helpers/frontiere.ts.
   */
  computeWindow: number | "all" | "none";
  /** Extraits publics de l'anonyme (listes de pronostics, faits marquants, chauds et froids) ; null = tout. */
  pronosticLists: number | null;
  highlights: number | null;
  hotCold: number | null;
  canChoosePeriod: boolean;
  /** Lignes demandées à /_api/tirages/list (le serveur borne ensuite au niveau). */
  listMaxRows: number;
  /** Sous-groupes détaillés jusqu'à ce niveau (3 triplets, 4 quartets, 5 quintés). */
  subsetMaxLevel: number;
  comparatorMaxNumbers: number;
  /** Lignes du baromètre des écarts ; 0 = toutes. */
  barometreRows: number;
  generatorMaxGrids: number;
  combinationSize: number;
  maxCombinations: number;
  historyDays: number;
  canReadDetailedAnalysis: boolean;
  canReadPremiumStats: boolean;
}

/** Limites des trois niveaux, telles que le serveur les applique. */
// Le JSON importé type computeWindow en string ou number : la forme est garantie par
// ExportEntitlementsCommandTest (fichier identique à EntitlementService::limitsByTier).
export const LIMITS_BY_TIER: Record<AccessTier, AccessLimits> = quotas as Record<AccessTier, AccessLimits>;

/** Limites d'un niveau. */
export function limitsFor(tier: AccessTier): AccessLimits {
  return LIMITS_BY_TIER[tier];
}

/** Miroir pur de EntitlementService::tierForStatus. */
export function deriveAccessTier(input: {
  isAuthenticated: boolean;
  hasFullAccess: boolean;
}): AccessTier {
  if (!input.isAuthenticated) return "anonymous";
  return input.hasFullAccess ? "premium" : "free";
}

/** Taille max de combinaison par niveau (kenometric.quotas.*_combination_size). */
export const COMBINATION_SIZE_LIMITS: Record<AccessTier, number> = {
  anonymous: LIMITS_BY_TIER.anonymous.combinationSize,
  free: LIMITS_BY_TIER.free.combinationSize,
  premium: LIMITS_BY_TIER.premium.combinationSize,
};

/** Taille max de combinaison analysable pour ce tier. */
export function maxCombinationSize(tier: AccessTier): number {
  return COMBINATION_SIZE_LIMITS[tier];
}
